/**
 * Catalogue items for every kind of merchant. One shape, whatever the trade —
 * `details` and `options` are plain label/value pairs, so a fleet, a fragrance
 * shelf and a menu all render through the same catalogue.
 */

export interface CatalogOptionValue {
  label: string
  price?: string
  stock?: number
}

/** Quick-service menu photography, kept apart so the chain set reads clearly. */
const FF = {
  whopper:
    'https://images.unsplash.com/photo-1571091718767-18b5b1457add?auto=format&fit=crop&q=80&w=900',
  burgerFlame:
    'https://images.unsplash.com/photo-1550547660-d9450f859349?auto=format&fit=crop&q=80&w=900',
  cheeseburger:
    'https://images.unsplash.com/photo-1586190848861-99aa4a171e90?auto=format&fit=crop&q=80&w=900',
  angus:
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&q=80&w=900',
  chicken:
    'https://images.unsplash.com/photo-1606755962773-d324e0a13086?auto=format&fit=crop&q=80&w=900',
  nuggets:
    'https://images.unsplash.com/photo-1562967914-608f82629710?auto=format&fit=crop&q=80&w=900',
  chickenFries:
    'https://images.unsplash.com/photo-1619881590738-a111d176d906?auto=format&fit=crop&q=80&w=900',
  plant:
    'https://images.unsplash.com/photo-1520072959219-c595dc870360?auto=format&fit=crop&q=80&w=900',
  fries:
    'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?auto=format&fit=crop&q=80&w=900',
  onionRings:
    'https://images.unsplash.com/photo-1639024471283-03518883512d?auto=format&fit=crop&q=80&w=900',
  drink:
    'https://images.unsplash.com/photo-1581006852262-e4307cf6283a?auto=format&fit=crop&q=80&w=900',
  coffee:
    'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?auto=format&fit=crop&q=80&w=900',
  sundae:
    'https://images.unsplash.com/photo-1563805042-7684c019e1cb?auto=format&fit=crop&q=80&w=900',
  shake:
    'https://images.unsplash.com/photo-1572490122747-3968b75cc699?auto=format&fit=crop&q=80&w=900',
  breakfast:
    'https://images.unsplash.com/photo-1550317138-10000687a72b?auto=format&fit=crop&q=80&w=900',
  pancakes:
    'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?auto=format&fit=crop&q=80&w=900',
  kids: 'https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5?auto=format&fit=crop&q=80&w=900',
  bundle:
    'https://images.unsplash.com/photo-1610614819513-58e34989848b?auto=format&fit=crop&q=80&w=900',
  meal: 'https://images.unsplash.com/photo-1603064752734-4c48eff53d05?auto=format&fit=crop&q=80&w=900',
  bites:
    'https://images.unsplash.com/photo-1608039755401-742074f0548d?auto=format&fit=crop&q=80&w=900',
}

export interface CatalogItem {
  id: string
  title: string
  /** Which blueprint drives the fields, pricing and availability. */
  kind: string
  category: string
  status: 'Active' | 'Draft' | 'Unavailable' | 'Archived'
  priceLabel: string
  image: string
  images: string[]
  description: string
  details: { label: string; value: string }[]
  options: { name: string; values: CatalogOptionValue[] }[]
  /** Plain-language reasons this item needs attention. */
  missing: string[]
  aiScore: number
  stock?: number
  talabPublished: boolean
  leads: number
}

const IMG = {
  prado:
    'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?auto=format&fit=crop&q=80&w=900',
  landcruiser:
    'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=900',
  camry:
    'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&q=80&w=900',
  van: 'https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&q=80&w=900',
  perfume:
    'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&q=80&w=900',
  perfume2:
    'https://images.unsplash.com/photo-1594035910387-fea47794261f?auto=format&fit=crop&q=80&w=900',
  burger:
    'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&q=80&w=900',
  salad:
    'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=900',
  shirt:
    'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?auto=format&fit=crop&q=80&w=900',
  dress:
    'https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&q=80&w=900',
  milk: 'https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&q=80&w=900',
  produce:
    'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80&w=900',
  jacket:
    'https://images.unsplash.com/photo-1551028719-00167b16eac5?auto=format&fit=crop&q=80&w=900',
  abaya:
    'https://images.unsplash.com/photo-1618436917352-cd3d11ea4d15?auto=format&fit=crop&q=80&w=900',
  sneakers:
    'https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&q=80&w=900',
  bag: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&q=80&w=900',
  linenShirt:
    'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?auto=format&fit=crop&q=80&w=900',
  bread:
    'https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=900',
  eggs: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?auto=format&fit=crop&q=80&w=900',
  coffee:
    'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?auto=format&fit=crop&q=80&w=900',
  water:
    'https://images.unsplash.com/photo-1523362628745-0c100150b504?auto=format&fit=crop&q=80&w=900',
  dates:
    'https://images.unsplash.com/photo-1609501676725-7186f017a4b7?auto=format&fit=crop&q=80&w=900',
  gwagon:
    'https://images.unsplash.com/photo-1520031441872-265e4ff70366?auto=format&fit=crop&q=80&w=900',
  bmw: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=900',
  tesla:
    'https://images.unsplash.com/photo-1560958089-b8a1929cea89?auto=format&fit=crop&q=80&w=900',
  patrol:
    'https://images.unsplash.com/photo-1612825173281-9a193378527e?auto=format&fit=crop&q=80&w=900',
  apartment:
    'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=900',
  villa:
    'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=900',
  townhouse:
    'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&q=80&w=900',
  penthouse:
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=900',
  studio:
    'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&q=80&w=900',
  office:
    'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=900',
  livingRoom:
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&q=80&w=900',
}

const d = (label: string, value: string) => ({
  label,
  value,
})

const RENTAL: CatalogItem[] = [
  {
    id: 'r1',
    title: 'Toyota Prado GXR 2025',
    kind: 'vehicle-rental',
    category: 'SUVs',
    status: 'Active',
    priceLabel: 'AED 350/day',
    image: IMG.prado,
    images: [IMG.prado, IMG.landcruiser],
    description:
      'Seven-seat SUV suited to family trips and desert weekends. Fully insured, serviced every 5,000 km and delivered anywhere in Dubai.',
    details: [
      d('Make', 'Toyota'),
      d('Model', 'Prado GXR'),
      d('Year', '2025'),
      d('Seats', '7'),
      d('Transmission', 'Automatic'),
      d('Fuel', 'Petrol'),
    ],
    options: [
      {
        name: 'Rental period',
        values: [
          {
            label: 'Daily',
            price: 'AED 350',
          },
          {
            label: 'Weekly',
            price: 'AED 2,100',
          },
          {
            label: 'Monthly',
            price: 'AED 7,500',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 94,
    talabPublished: true,
    leads: 12,
  },
  {
    id: 'r2',
    title: 'Toyota Land Cruiser VXR 2024',
    kind: 'vehicle-rental',
    category: 'Luxury',
    status: 'Active',
    priceLabel: 'AED 650/day',
    image: IMG.landcruiser,
    images: [IMG.landcruiser],
    description:
      'Flagship SUV with full leather interior, ideal for executive travel and long desert drives.',
    details: [
      d('Make', 'Toyota'),
      d('Model', 'Land Cruiser VXR'),
      d('Year', '2024'),
      d('Seats', '7'),
      d('Transmission', 'Automatic'),
      d('Fuel', 'Petrol'),
    ],
    options: [],
    missing: ['Add insurance cover'],
    aiScore: 78,
    talabPublished: true,
    leads: 9,
  },
  {
    id: 'r3',
    title: 'Toyota Camry 2024',
    kind: 'vehicle-rental',
    category: 'Sedans',
    status: 'Active',
    priceLabel: 'AED 180/day',
    image: IMG.camry,
    images: [IMG.camry],
    description:
      'Economical sedan for city driving, with free delivery inside Dubai on weekly bookings.',
    details: [
      d('Make', 'Toyota'),
      d('Model', 'Camry'),
      d('Year', '2024'),
      d('Seats', '5'),
      d('Transmission', 'Automatic'),
      d('Fuel', 'Petrol'),
    ],
    options: [],
    missing: [],
    aiScore: 91,
    talabPublished: true,
    leads: 21,
  },
  {
    id: 'r4',
    title: 'Toyota Hiace 13-Seater 2023',
    kind: 'vehicle-rental',
    category: 'Vans',
    status: 'Draft',
    priceLabel: 'AED 420/day',
    image: IMG.van,
    images: [IMG.van],
    description: '',
    details: [
      d('Make', 'Toyota'),
      d('Model', 'Hiace'),
      d('Year', '2023'),
      d('Seats', '13'),
    ],
    options: [],
    missing: ['Add a description', 'Add more photos'],
    aiScore: 52,
    talabPublished: false,
    leads: 0,
  },
]

const PERFUME: CatalogItem[] = [
  {
    id: 'p1',
    title: 'Oud Royale Extrait',
    kind: 'perfume',
    category: 'Oud',
    status: 'Active',
    priceLabel: 'AED 520',
    image: IMG.perfume,
    images: [IMG.perfume, IMG.perfume2],
    description:
      'Aged Cambodian agarwood layered with rose and saffron. Built for evenings and cooler months.',
    details: [
      d('Brand', 'Maison Aroma'),
      d('Concentration', 'Extrait de Parfum'),
      d('Size', '100 ml'),
      d('Fragrance family', 'Woody Oriental'),
      d('Gender', 'Unisex'),
      d('Main notes', 'Oud · Rose · Saffron'),
    ],
    options: [
      {
        name: 'Bottle size',
        values: [
          {
            label: '50 ml',
            price: 'AED 320',
            stock: 14,
          },
          {
            label: '100 ml',
            price: 'AED 520',
            stock: 9,
          },
          {
            label: '200 ml',
            price: 'AED 880',
            stock: 3,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 92,
    stock: 26,
    talabPublished: true,
    leads: 7,
  },
  {
    id: 'p2',
    title: 'Fleur Blanche Eau de Parfum',
    kind: 'perfume',
    category: 'Floral',
    status: 'Active',
    priceLabel: 'AED 340',
    image: IMG.perfume2,
    images: [IMG.perfume2],
    description:
      'Jasmine and tuberose over a soft musk base — a daytime floral that stays close to the skin.',
    details: [
      d('Brand', 'Maison Aroma'),
      d('Concentration', 'Eau de Parfum'),
      d('Size', '75 ml'),
      d('Fragrance family', 'White Floral'),
      d('Gender', 'Women'),
    ],
    options: [],
    missing: ['Add fragrance notes'],
    aiScore: 74,
    stock: 4,
    talabPublished: true,
    leads: 3,
  },
  {
    id: 'p3',
    title: 'Discovery Gift Set — 5 × 10 ml',
    kind: 'perfume',
    category: 'Gift Sets',
    status: 'Unavailable',
    priceLabel: 'AED 260',
    image: IMG.perfume,
    images: [IMG.perfume],
    description: 'Five signature scents in travel sizes, boxed for gifting.',
    details: [
      d('Brand', 'Maison Aroma'),
      d('Size', '5 × 10 ml'),
      d('Gender', 'Unisex'),
    ],
    options: [],
    missing: [],
    aiScore: 86,
    stock: 0,
    talabPublished: true,
    leads: 1,
  },
]

const RESTAURANT: CatalogItem[] = [
  {
    id: 'f1',
    title: 'Truffle Smash Burger',
    kind: 'restaurant',
    category: 'Burgers',
    status: 'Active',
    priceLabel: 'AED 52',
    image: IMG.burger,
    images: [IMG.burger],
    description:
      'Two smashed patties, aged cheddar and truffle aioli in a brioche bun. Served with fries.',
    details: [
      d('Cuisine', 'American'),
      d('Portion', 'Single · 220g'),
      d('Preparation time', '15 min'),
      d('Allergens', 'Gluten · Dairy · Egg'),
      d('Spice level', 'Mild'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: 'Regular',
            price: 'AED 52',
          },
          {
            label: 'Double',
            price: 'AED 68',
          },
        ],
      },
      {
        name: 'Extras',
        values: [
          {
            label: 'Extra cheese',
            price: 'AED 6',
          },
          {
            label: 'Truffle fries',
            price: 'AED 18',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 90,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'f2',
    title: 'Mezze Platter for Four',
    kind: 'restaurant',
    category: 'Starters',
    status: 'Active',
    priceLabel: 'AED 145',
    image: IMG.salad,
    images: [IMG.salad],
    description:
      'Hummus, moutabel, tabbouleh, vine leaves and warm bread, arranged to share.',
    details: [
      d('Cuisine', 'Levantine'),
      d('Portion', 'Serves 4'),
      d('Preparation time', '25 min'),
      d('Dietary', 'Vegetarian'),
    ],
    options: [],
    missing: ['Add allergens'],
    aiScore: 68,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'f3',
    title: 'Pistachio Basbousa',
    kind: 'restaurant',
    category: 'Desserts',
    status: 'Unavailable',
    priceLabel: 'AED 32',
    image: IMG.salad,
    images: [IMG.salad],
    description: 'Semolina cake soaked in orange blossom syrup, topped with pistachio.',
    details: [
      d('Cuisine', 'Levantine'),
      d('Portion', 'Single'),
      d('Preparation time', '5 min'),
      d('Allergens', 'Nuts · Gluten · Dairy'),
    ],
    options: [],
    missing: [],
    aiScore: 88,
    talabPublished: false,
    leads: 0,
  },
]

/**
 * A quick-service chain — the case that stresses the category view hardest.
 * One menu, nine departments, and the same item repeated as a meal, so the
 * category row is the only sane way to navigate it.
 */
const FASTFOOD: CatalogItem[] = [
  // ── Burgers ────────────────────────────────────────────────────────────
  {
    id: 'bk1',
    title: 'Whopper',
    kind: 'restaurant',
    category: 'Burgers',
    status: 'Active',
    priceLabel: 'AED 24',
    image: FF.whopper,
    images: [FF.whopper, FF.burgerFlame],
    description:
      'Flame-grilled beef patty with tomatoes, lettuce, mayo, pickles and sliced onion on a sesame seed bun.',
    details: [
      d('Cuisine', 'American'),
      d('Portion', 'Single · 280g'),
      d('Preparation time', '6 min'),
      d('Allergens', 'Gluten · Egg · Sesame'),
      d('Calories', '657 kcal'),
      d('Spice level', 'None'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          { label: 'Single', price: 'AED 24' },
          { label: 'Double', price: 'AED 32' },
          { label: 'Triple', price: 'AED 39' },
        ],
      },
      {
        name: 'Extras',
        values: [
          { label: 'Extra cheese', price: 'AED 3' },
          { label: 'Bacon', price: 'AED 5' },
          { label: 'Jalapeños', price: 'AED 2' },
        ],
      },
    ],
    missing: [],
    aiScore: 96,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk2',
    title: 'Whopper Meal',
    kind: 'restaurant',
    category: 'Meals',
    status: 'Active',
    priceLabel: 'AED 34',
    image: FF.meal,
    images: [FF.meal],
    description:
      'Whopper with medium fries and a medium soft drink of your choice.',
    details: [
      d('Portion', 'Meal for one'),
      d('Preparation time', '7 min'),
      d('Allergens', 'Gluten · Egg · Sesame'),
      d('Calories', '1,120 kcal'),
    ],
    options: [
      {
        name: 'Meal size',
        values: [
          { label: 'Medium', price: 'AED 34' },
          { label: 'Large', price: 'AED 39' },
        ],
      },
      {
        name: 'Drink',
        values: [
          { label: 'Pepsi' },
          { label: 'Pepsi Max' },
          { label: '7UP' },
          { label: 'Mirinda' },
          { label: 'Water' },
        ],
      },
      {
        name: 'Side',
        values: [
          { label: 'Fries' },
          { label: 'Onion rings', price: 'AED 3' },
          { label: 'Cheesy fries', price: 'AED 5' },
        ],
      },
    ],
    missing: [],
    aiScore: 93,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk3',
    title: 'Double Cheeseburger',
    kind: 'restaurant',
    category: 'Burgers',
    status: 'Active',
    priceLabel: 'AED 19',
    image: FF.cheeseburger,
    images: [FF.cheeseburger],
    description:
      'Two flame-grilled beef patties with melted American cheese, pickles, ketchup and mustard.',
    details: [
      d('Cuisine', 'American'),
      d('Portion', 'Single · 190g'),
      d('Preparation time', '5 min'),
      d('Allergens', 'Gluten · Dairy · Mustard'),
      d('Calories', '437 kcal'),
    ],
    options: [
      {
        name: 'Extras',
        values: [
          { label: 'Extra patty', price: 'AED 7' },
          { label: 'Extra cheese', price: 'AED 3' },
        ],
      },
    ],
    missing: [],
    aiScore: 91,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk4',
    title: 'Steakhouse Angus',
    kind: 'restaurant',
    category: 'Burgers',
    status: 'Active',
    priceLabel: 'AED 36',
    image: FF.angus,
    images: [FF.angus],
    description:
      'Angus beef patty with smoked cheddar, crispy onions, bacon and steakhouse sauce on a brioche bun.',
    details: [
      d('Cuisine', 'American'),
      d('Portion', 'Single · 310g'),
      d('Preparation time', '8 min'),
      d('Allergens', 'Gluten · Dairy · Egg'),
      d('Calories', '790 kcal'),
    ],
    options: [],
    missing: ['Add a photo of the current recipe'],
    aiScore: 74,
    talabPublished: true,
    leads: 0,
  },
  // ── Chicken ───────────────────────────────────────────────────────────
  {
    id: 'bk5',
    title: 'Crispy Chicken Royale',
    kind: 'restaurant',
    category: 'Chicken',
    status: 'Active',
    priceLabel: 'AED 22',
    image: FF.chicken,
    images: [FF.chicken],
    description:
      'Crispy chicken fillet with lettuce and creamy mayo in a long sesame bun.',
    details: [
      d('Cuisine', 'American'),
      d('Portion', 'Single · 230g'),
      d('Preparation time', '7 min'),
      d('Allergens', 'Gluten · Egg · Sesame'),
      d('Calories', '590 kcal'),
      d('Spice level', 'None'),
    ],
    options: [
      {
        name: 'Style',
        values: [
          { label: 'Original' },
          { label: 'Spicy' },
          { label: 'Zinger', price: 'AED 2' },
        ],
      },
    ],
    missing: [],
    aiScore: 94,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk6',
    title: 'Chicken Nuggets · 9 pieces',
    kind: 'restaurant',
    category: 'Chicken',
    status: 'Active',
    priceLabel: 'AED 17',
    image: FF.nuggets,
    images: [FF.nuggets],
    description:
      'Nine breaded chicken nuggets with your choice of dipping sauce.',
    details: [
      d('Portion', '9 pieces'),
      d('Preparation time', '5 min'),
      d('Allergens', 'Gluten'),
      d('Calories', '395 kcal'),
    ],
    options: [
      {
        name: 'Count',
        values: [
          { label: '6 pieces', price: 'AED 13' },
          { label: '9 pieces', price: 'AED 17' },
          { label: '20 pieces', price: 'AED 33' },
        ],
      },
      {
        name: 'Dip',
        values: [
          { label: 'Barbecue' },
          { label: 'Sweet chilli' },
          { label: 'Garlic mayo' },
          { label: 'Ranch' },
        ],
      },
    ],
    missing: [],
    aiScore: 92,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk7',
    title: 'Chicken Fries',
    kind: 'restaurant',
    category: 'Chicken',
    status: 'Unavailable',
    priceLabel: 'AED 15',
    image: FF.chickenFries,
    images: [FF.chickenFries],
    description: 'Seasoned chicken strips in a fry shape, served in a carton.',
    details: [
      d('Portion', '9 pieces'),
      d('Preparation time', '5 min'),
      d('Allergens', 'Gluten'),
    ],
    options: [],
    missing: [],
    aiScore: 85,
    talabPublished: true,
    leads: 0,
  },
  // ── Plant-Based ───────────────────────────────────────────────────────
  {
    id: 'bk8',
    title: 'Plant-Based Whopper',
    kind: 'restaurant',
    category: 'Plant-Based',
    status: 'Active',
    priceLabel: 'AED 27',
    image: FF.plant,
    images: [FF.plant],
    description:
      'Flame-grilled plant-based patty with the same tomatoes, lettuce, mayo, pickles and onion.',
    details: [
      d('Cuisine', 'American'),
      d('Portion', 'Single · 270g'),
      d('Preparation time', '7 min'),
      d('Dietary', 'Vegetarian'),
      d('Allergens', 'Gluten · Soy · Egg · Sesame'),
      d('Calories', '610 kcal'),
    ],
    options: [
      {
        name: 'Make it vegan',
        values: [{ label: 'Without mayo' }],
      },
    ],
    missing: [],
    aiScore: 90,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk9',
    title: 'Veggie Royale',
    kind: 'restaurant',
    category: 'Plant-Based',
    status: 'Draft',
    priceLabel: 'AED 23',
    image: FF.plant,
    images: [FF.plant],
    description:
      'Crispy vegetable patty with lettuce and mayo in a long sesame bun.',
    details: [d('Portion', 'Single · 220g'), d('Dietary', 'Vegetarian')],
    options: [],
    missing: ['Add allergens', 'Add a photo'],
    aiScore: 41,
    talabPublished: false,
    leads: 0,
  },
  // ── Sides ─────────────────────────────────────────────────────────────
  {
    id: 'bk10',
    title: 'French Fries',
    kind: 'restaurant',
    category: 'Sides',
    status: 'Active',
    priceLabel: 'AED 9',
    image: FF.fries,
    images: [FF.fries],
    description: 'Golden salted fries, served hot.',
    details: [
      d('Portion', 'Medium'),
      d('Preparation time', '3 min'),
      d('Dietary', 'Vegetarian'),
      d('Calories', '312 kcal'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          { label: 'Small', price: 'AED 7' },
          { label: 'Medium', price: 'AED 9' },
          { label: 'Large', price: 'AED 12' },
        ],
      },
    ],
    missing: [],
    aiScore: 95,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk11',
    title: 'Onion Rings',
    kind: 'restaurant',
    category: 'Sides',
    status: 'Active',
    priceLabel: 'AED 11',
    image: FF.onionRings,
    images: [FF.onionRings],
    description: 'Battered onion rings with a sweet chilli dip.',
    details: [
      d('Portion', 'Medium · 8 pieces'),
      d('Preparation time', '4 min'),
      d('Dietary', 'Vegetarian'),
      d('Allergens', 'Gluten'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          { label: 'Medium', price: 'AED 11' },
          { label: 'Large', price: 'AED 14' },
        ],
      },
    ],
    missing: [],
    aiScore: 89,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk12',
    title: 'Cheesy Fries',
    kind: 'restaurant',
    category: 'Sides',
    status: 'Active',
    priceLabel: 'AED 14',
    image: FF.fries,
    images: [FF.fries],
    description: 'Fries loaded with melted cheese sauce.',
    details: [
      d('Portion', 'Medium'),
      d('Preparation time', '4 min'),
      d('Allergens', 'Dairy'),
    ],
    options: [],
    missing: ['Add calories'],
    aiScore: 72,
    talabPublished: true,
    leads: 0,
  },
  // ── Drinks ────────────────────────────────────────────────────────────
  {
    id: 'bk13',
    title: 'Pepsi',
    kind: 'restaurant',
    category: 'Drinks',
    status: 'Active',
    priceLabel: 'AED 8',
    image: FF.drink,
    images: [FF.drink],
    description: 'Chilled Pepsi, served over ice.',
    details: [d('Portion', 'Medium · 400 ml'), d('Preparation time', '1 min')],
    options: [
      {
        name: 'Size',
        values: [
          { label: 'Small', price: 'AED 6' },
          { label: 'Medium', price: 'AED 8' },
          { label: 'Large', price: 'AED 10' },
        ],
      },
    ],
    missing: [],
    aiScore: 88,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk14',
    title: 'Iced Latte',
    kind: 'restaurant',
    category: 'Drinks',
    status: 'Active',
    priceLabel: 'AED 13',
    image: FF.coffee,
    images: [FF.coffee],
    description: 'Espresso over ice with cold milk.',
    details: [
      d('Portion', 'Medium · 350 ml'),
      d('Preparation time', '3 min'),
      d('Allergens', 'Dairy'),
    ],
    options: [
      {
        name: 'Milk',
        values: [
          { label: 'Full fat' },
          { label: 'Skimmed' },
          { label: 'Oat', price: 'AED 3' },
        ],
      },
    ],
    missing: [],
    aiScore: 86,
    talabPublished: true,
    leads: 0,
  },
  // ── Desserts ──────────────────────────────────────────────────────────
  {
    id: 'bk15',
    title: 'Oreo Sundae',
    kind: 'restaurant',
    category: 'Desserts',
    status: 'Active',
    priceLabel: 'AED 12',
    image: FF.sundae,
    images: [FF.sundae],
    description: 'Soft vanilla ice cream with crushed Oreo and chocolate sauce.',
    details: [
      d('Portion', 'Single'),
      d('Preparation time', '2 min'),
      d('Allergens', 'Dairy · Gluten'),
      d('Calories', '289 kcal'),
    ],
    options: [
      {
        name: 'Sauce',
        values: [
          { label: 'Chocolate' },
          { label: 'Caramel' },
          { label: 'Strawberry' },
        ],
      },
    ],
    missing: [],
    aiScore: 90,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk16',
    title: 'Chocolate Milkshake',
    kind: 'restaurant',
    category: 'Desserts',
    status: 'Active',
    priceLabel: 'AED 16',
    image: FF.shake,
    images: [FF.shake],
    description: 'Thick chocolate shake made with soft-serve ice cream.',
    details: [
      d('Portion', 'Medium · 400 ml'),
      d('Preparation time', '3 min'),
      d('Allergens', 'Dairy'),
    ],
    options: [
      {
        name: 'Flavour',
        values: [
          { label: 'Chocolate' },
          { label: 'Vanilla' },
          { label: 'Strawberry' },
          { label: 'Oreo', price: 'AED 3' },
        ],
      },
    ],
    missing: [],
    aiScore: 87,
    talabPublished: true,
    leads: 0,
  },
  // ── Breakfast ─────────────────────────────────────────────────────────
  {
    id: 'bk17',
    title: 'Egg & Cheese Muffin',
    kind: 'restaurant',
    category: 'Breakfast',
    status: 'Active',
    priceLabel: 'AED 13',
    image: FF.breakfast,
    images: [FF.breakfast],
    description:
      'Folded egg and American cheese in a toasted English muffin. Served until 11am.',
    details: [
      d('Portion', 'Single'),
      d('Preparation time', '4 min'),
      d('Allergens', 'Gluten · Egg · Dairy'),
      d('Served', 'Until 11:00'),
    ],
    options: [
      {
        name: 'Add',
        values: [
          { label: 'Beef bacon', price: 'AED 5' },
          { label: 'Chicken sausage', price: 'AED 5' },
        ],
      },
    ],
    missing: [],
    aiScore: 88,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'bk18',
    title: 'Pancakes · 3 pieces',
    kind: 'restaurant',
    category: 'Breakfast',
    status: 'Unavailable',
    priceLabel: 'AED 15',
    image: FF.pancakes,
    images: [FF.pancakes],
    description: 'Three fluffy pancakes with maple syrup and butter.',
    details: [
      d('Portion', '3 pieces'),
      d('Allergens', 'Gluten · Egg · Dairy'),
      d('Served', 'Until 11:00'),
    ],
    options: [],
    missing: [],
    aiScore: 84,
    talabPublished: true,
    leads: 0,
  },
  // ── Kids ──────────────────────────────────────────────────────────────
  {
    id: 'bk19',
    title: 'Kids Nugget Meal',
    kind: 'restaurant',
    category: 'Kids',
    status: 'Active',
    priceLabel: 'AED 21',
    image: FF.kids,
    images: [FF.kids],
    description:
      'Four nuggets, small fries, a small drink and a toy, in a kids box.',
    details: [
      d('Portion', 'Kids meal'),
      d('Preparation time', '5 min'),
      d('Allergens', 'Gluten'),
      d('Calories', '480 kcal'),
    ],
    options: [
      {
        name: 'Drink',
        values: [
          { label: 'Apple juice' },
          { label: 'Water' },
          { label: 'Milk' },
        ],
      },
    ],
    missing: [],
    aiScore: 91,
    talabPublished: true,
    leads: 0,
  },
  // ── Sharing ───────────────────────────────────────────────────────────
  {
    id: 'bk20',
    title: 'Family Bundle · Feeds 4',
    kind: 'restaurant',
    category: 'Sharing',
    status: 'Active',
    priceLabel: 'AED 119',
    image: FF.bundle,
    images: [FF.bundle],
    description:
      'Two Whoppers, two Chicken Royales, four medium fries, nine nuggets and four drinks.',
    details: [
      d('Portion', 'Feeds 4'),
      d('Preparation time', '12 min'),
      d('Allergens', 'Gluten · Egg · Dairy · Sesame'),
    ],
    options: [
      {
        name: 'Upgrade',
        values: [
          { label: 'All large fries', price: 'AED 12' },
          { label: 'Add 4 sundaes', price: 'AED 40' },
        ],
      },
    ],
    missing: [],
    aiScore: 93,
    talabPublished: true,
    leads: 2,
  },
  // ── Uncategorized — the import that nobody has filed yet ──────────────
  {
    id: 'bk21',
    title: 'Spicy Chilli Cheese Bites',
    kind: 'restaurant',
    category: '',
    status: 'Draft',
    priceLabel: 'AED 14',
    image: FF.bites,
    images: [FF.bites],
    description: '',
    details: [d('Portion', '6 pieces')],
    options: [],
    missing: ['Choose a category', 'Add a description', 'Add allergens'],
    aiScore: 28,
    talabPublished: false,
    leads: 0,
  },
]

const RETAIL: CatalogItem[] = [
  {
    id: 'c1',
    title: 'Classic Linen Shirt',
    kind: 'clothing',
    category: 'Shirts',
    status: 'Active',
    priceLabel: 'From AED 180',
    image: IMG.shirt,
    images: [IMG.shirt],
    description:
      'Relaxed-fit shirt in washed European linen, cut to stay cool through summer.',
    details: [
      d('Brand', 'Atelier Nine'),
      d('Material', '100% linen'),
      d('Fit', 'Relaxed'),
      d('Gender', 'Men'),
      d('Season', 'Spring / Summer'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: 'S',
            stock: 6,
          },
          {
            label: 'M',
            stock: 11,
          },
          {
            label: 'L',
            stock: 4,
          },
          {
            label: 'XL',
            stock: 0,
          },
        ],
      },
      {
        name: 'Colour',
        values: [
          {
            label: 'White',
          },
          {
            label: 'Sand',
          },
          {
            label: 'Navy',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 89,
    stock: 21,
    talabPublished: true,
    leads: 2,
  },
  {
    id: 'c2',
    title: 'Silk Midi Dress',
    kind: 'clothing',
    category: 'Dresses',
    status: 'Active',
    priceLabel: 'AED 640',
    image: IMG.dress,
    images: [IMG.dress],
    description: '',
    details: [
      d('Brand', 'Atelier Nine'),
      d('Material', 'Mulberry silk'),
      d('Fit', 'True to size'),
      d('Gender', 'Women'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: 'XS',
            stock: 2,
          },
          {
            label: 'S',
            stock: 3,
          },
          {
            label: 'M',
            stock: 1,
          },
        ],
      },
    ],
    missing: ['Add a description', 'Add a size chart'],
    aiScore: 58,
    stock: 6,
    talabPublished: false,
    leads: 0,
  },
  {
    id: 'g1',
    title: 'Organic Whole Milk 1L',
    kind: 'grocery',
    category: 'Dairy',
    status: 'Active',
    priceLabel: 'AED 12',
    image: IMG.milk,
    images: [IMG.milk],
    description: 'Cold-chain organic whole milk from Al Ain, restocked daily.',
    details: [
      d('Brand', 'Marmum Organic'),
      d('Weight', '1 L'),
      d('Unit', 'Bottle'),
      d('Pack size', 'Single'),
      d('Storage', 'Keep refrigerated'),
    ],
    options: [
      {
        name: 'Pack size',
        values: [
          {
            label: 'Single',
            price: 'AED 12',
            stock: 48,
          },
          {
            label: '6-pack',
            price: 'AED 66',
            stock: 12,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 87,
    stock: 48,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'g2',
    title: 'Seasonal Produce Box',
    kind: 'grocery',
    category: 'Fresh Produce',
    status: 'Active',
    priceLabel: 'AED 89',
    image: IMG.produce,
    images: [IMG.produce],
    description:
      'A weekly box of whatever is best that week — usually eight to ten kinds of fruit and vegetables.',
    details: [
      d('Brand', 'Talab Fresh'),
      d('Weight', '6–7 kg'),
      d('Pack size', 'Box'),
      d('Origin', 'UAE & Jordan'),
    ],
    options: [],
    missing: ['Add storage advice'],
    aiScore: 72,
    stock: 3,
    talabPublished: true,
    leads: 1,
  },
]

/* ------------------------------------------------------------------ apparel */

const APPAREL: CatalogItem[] = [
  {
    id: 'a1',
    title: 'Oversized Linen Shirt',
    kind: 'clothing',
    category: 'Men',
    status: 'Active',
    priceLabel: 'AED 240',
    image: IMG.linenShirt,
    images: [IMG.linenShirt, IMG.shirt],
    description:
      'Breathable European linen cut with a relaxed shoulder. Pre-washed so it softens rather than shrinks.',
    details: [
      d('Material', '100% linen'),
      d('Fit', 'Relaxed'),
      d('Season', 'Spring / Summer'),
      d('Care', 'Machine wash cold'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: 'S',
            stock: 6,
          },
          {
            label: 'M',
            stock: 11,
          },
          {
            label: 'L',
            stock: 9,
          },
          {
            label: 'XL',
            stock: 2,
          },
        ],
      },
      {
        name: 'Colour',
        values: [
          {
            label: 'Sand',
            stock: 16,
          },
          {
            label: 'White',
            stock: 12,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 92,
    stock: 28,
    talabPublished: true,
    leads: 7,
  },
  {
    id: 'a2',
    title: 'Embroidered Open Abaya',
    kind: 'clothing',
    category: 'Women',
    status: 'Active',
    priceLabel: 'AED 690',
    image: IMG.abaya,
    images: [IMG.abaya, IMG.dress],
    description:
      'Flowing crepe abaya with hand-finished sleeve embroidery. Sold with a matching shayla.',
    details: [
      d('Material', 'Nida crepe'),
      d('Fit', 'Straight'),
      d('Season', 'All year'),
      d('Length', '56 in'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: '52',
            stock: 3,
          },
          {
            label: '54',
            stock: 5,
          },
          {
            label: '56',
            stock: 4,
          },
        ],
      },
    ],
    missing: ['Add care instructions'],
    aiScore: 78,
    stock: 12,
    talabPublished: true,
    leads: 14,
  },
  {
    id: 'a3',
    title: 'Court Leather Sneakers',
    kind: 'clothing',
    category: 'Shoes',
    status: 'Active',
    priceLabel: 'AED 520',
    image: IMG.sneakers,
    images: [IMG.sneakers],
    description:
      'Full-grain leather court shoe on a cupsole, with a padded collar and removable insole.',
    details: [
      d('Material', 'Full-grain leather'),
      d('Fit', 'True to size'),
      d('Sole', 'Rubber cupsole'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: '40',
            stock: 2,
          },
          {
            label: '41',
            stock: 4,
          },
          {
            label: '42',
            stock: 6,
          },
          {
            label: '43',
            stock: 1,
          },
        ],
      },
    ],
    missing: ['Add more photos'],
    aiScore: 71,
    stock: 13,
    talabPublished: true,
    leads: 5,
  },
  {
    id: 'a4',
    title: 'Quilted Bomber Jacket',
    kind: 'clothing',
    category: 'Men',
    status: 'Active',
    priceLabel: 'AED 810',
    image: IMG.jacket,
    images: [IMG.jacket],
    description:
      'Lightly quilted bomber with a ribbed hem, sized to layer over a shirt through the cooler months.',
    details: [
      d('Material', 'Recycled polyester'),
      d('Fit', 'Regular'),
      d('Season', 'Autumn / Winter'),
    ],
    options: [
      {
        name: 'Size',
        values: [
          {
            label: 'M',
            stock: 4,
          },
          {
            label: 'L',
            stock: 3,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 88,
    stock: 7,
    talabPublished: true,
    leads: 3,
  },
  {
    id: 'a5',
    title: 'Structured Tote Bag',
    kind: 'clothing',
    category: 'Accessories',
    status: 'Active',
    priceLabel: 'AED 1,150',
    image: IMG.bag,
    images: [IMG.bag],
    description:
      'Saffiano-textured tote with a detachable pouch. Fits a 14-inch laptop.',
    details: [
      d('Material', 'Saffiano leather'),
      d('Dimensions', '34 × 27 × 12 cm'),
      d('Hardware', 'Brushed gold'),
    ],
    options: [
      {
        name: 'Colour',
        values: [
          {
            label: 'Black',
            stock: 5,
          },
          {
            label: 'Taupe',
            stock: 2,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 90,
    stock: 7,
    talabPublished: true,
    leads: 9,
  },
  {
    id: 'a6',
    title: 'Midi Slip Dress',
    kind: 'clothing',
    category: 'Women',
    status: 'Draft',
    priceLabel: 'AED 430',
    image: IMG.dress,
    images: [IMG.dress],
    description: '',
    details: [d('Material', 'Satin viscose')],
    options: [],
    missing: ['Add a description', 'Add sizes and stock', 'Add more photos'],
    aiScore: 34,
    stock: 0,
    talabPublished: false,
    leads: 0,
  },
  {
    id: 'a7',
    title: 'Ribbed Knit Polo',
    kind: 'clothing',
    category: '',
    status: 'Active',
    priceLabel: 'AED 310',
    image: IMG.shirt,
    images: [IMG.shirt],
    description:
      'Fine-gauge ribbed polo in a cotton-modal blend, imported and not yet sorted into a category.',
    details: [d('Material', 'Cotton modal'), d('Fit', 'Slim')],
    options: [],
    missing: ['Choose a category'],
    aiScore: 62,
    stock: 18,
    talabPublished: false,
    leads: 1,
  },
]

/* ------------------------------------------------------------------ grocery */

const GROCERY: CatalogItem[] = [
  {
    id: 'g1',
    title: 'Fresh Whole Milk 2L',
    kind: 'grocery',
    category: 'Dairy',
    status: 'Active',
    priceLabel: 'AED 12.50',
    image: IMG.milk,
    images: [IMG.milk],
    description:
      'Locally bottled full-fat milk, delivered chilled within 12 hours of pasteurising.',
    details: [
      d('Brand', 'Al Rawabi'),
      d('Weight', '2 L'),
      d('Pack size', 'Single'),
      d('Storage', 'Keep refrigerated'),
      d('Origin', 'UAE'),
    ],
    options: [
      {
        name: 'Pack size',
        values: [
          {
            label: 'Single',
            price: 'AED 12.50',
            stock: 84,
          },
          {
            label: '6-pack',
            price: 'AED 68.00',
            stock: 22,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 93,
    stock: 84,
    talabPublished: true,
    leads: 2,
  },
  {
    id: 'g2',
    title: 'Seasonal Fruit & Veg Box',
    kind: 'grocery',
    category: 'Fresh Produce',
    status: 'Active',
    priceLabel: 'AED 95.00',
    image: IMG.produce,
    images: [IMG.produce],
    description:
      'A weekly box of twelve seasonal lines, picked that morning from local farms.',
    details: [
      d('Weight', '≈ 7 kg'),
      d('Pack size', '12 lines'),
      d('Origin', 'UAE and Jordan'),
      d('Storage', 'Refrigerate on arrival'),
    ],
    options: [
      {
        name: 'Box size',
        values: [
          {
            label: 'Small',
            price: 'AED 65.00',
            stock: 18,
          },
          {
            label: 'Family',
            price: 'AED 95.00',
            stock: 11,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 89,
    stock: 29,
    talabPublished: true,
    leads: 8,
  },
  {
    id: 'g3',
    title: 'Sourdough Bloomer',
    kind: 'grocery',
    category: 'Bakery',
    status: 'Active',
    priceLabel: 'AED 18.00',
    image: IMG.bread,
    images: [IMG.bread],
    description:
      'Baked each morning on a 24-hour ferment. Best eaten the day it arrives.',
    details: [
      d('Weight', '800 g'),
      d('Pack size', 'Single loaf'),
      d('Storage', 'Room temperature'),
      d('Expiry', '2 days'),
    ],
    options: [],
    missing: ['Add allergen information'],
    aiScore: 74,
    stock: 6,
    talabPublished: true,
    leads: 1,
  },
  {
    id: 'g4',
    title: 'Free-Range Eggs, 30 pcs',
    kind: 'grocery',
    category: 'Dairy',
    status: 'Active',
    priceLabel: 'AED 34.00',
    image: IMG.eggs,
    images: [IMG.eggs],
    description: 'Large free-range eggs from farms in Al Ain, tray of thirty.',
    details: [
      d('Brand', 'Golden Farm'),
      d('Pack size', 'Tray of 30'),
      d('Origin', 'UAE'),
      d('Storage', 'Keep refrigerated'),
    ],
    options: [],
    missing: [],
    aiScore: 87,
    stock: 41,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'g5',
    title: 'Single Origin Coffee Beans 1kg',
    kind: 'grocery',
    category: 'Beverages',
    status: 'Active',
    priceLabel: 'AED 120.00',
    image: IMG.coffee,
    images: [IMG.coffee],
    description:
      'Ethiopian Yirgacheffe, roasted weekly in Dubai. Notes of jasmine and stone fruit.',
    details: [
      d('Brand', 'Nightjar'),
      d('Weight', '1 kg'),
      d('Origin', 'Ethiopia'),
      d('Storage', 'Cool and dry'),
    ],
    options: [
      {
        name: 'Grind',
        values: [
          {
            label: 'Whole bean',
            stock: 14,
          },
          {
            label: 'Filter',
            stock: 6,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 91,
    stock: 20,
    talabPublished: true,
    leads: 4,
  },
  {
    id: 'g6',
    title: 'Mineral Water 24 × 500ml',
    kind: 'grocery',
    category: 'Beverages',
    status: 'Active',
    priceLabel: 'AED 22.00',
    image: IMG.water,
    images: [IMG.water],
    description: 'Case of twenty-four bottles. Bulk pricing from four cases.',
    details: [
      d('Brand', 'Masafi'),
      d('Pack size', '24 × 500 ml'),
      d('Origin', 'UAE'),
    ],
    options: [
      {
        name: 'Pack size',
        values: [
          {
            label: '24-pack',
            price: 'AED 22.00',
            stock: 120,
          },
          {
            label: '4 cases',
            price: 'AED 80.00',
            stock: 30,
          },
        ],
      },
    ],
    missing: [],
    aiScore: 85,
    stock: 120,
    talabPublished: true,
    leads: 0,
  },
  {
    id: 'g7',
    title: 'Medjool Dates 1kg',
    kind: 'grocery',
    category: 'Pantry',
    status: 'Unavailable',
    priceLabel: 'AED 78.00',
    image: IMG.dates,
    images: [IMG.dates],
    description:
      'Jumbo Medjool dates in a gift-ready box. Back in stock next week.',
    details: [d('Weight', '1 kg'), d('Origin', 'Jordan')],
    options: [],
    missing: ['Add pack size'],
    aiScore: 66,
    stock: 0,
    talabPublished: true,
    leads: 3,
  },
  {
    id: 'g8',
    title: 'Basmati Rice 5kg',
    kind: 'grocery',
    category: '',
    status: 'Draft',
    priceLabel: 'AED 46.00',
    image: '',
    images: [],
    description: '',
    details: [d('Weight', '5 kg')],
    options: [],
    missing: ['Add a photo', 'Add a description', 'Choose a category'],
    aiScore: 28,
    stock: 52,
    talabPublished: false,
    leads: 0,
  },
]

/* ------------------------------------------------------------ cars for sale */

const CARS_SALE: CatalogItem[] = [
  {
    id: 's1',
    title: 'Mercedes-Benz G 63 AMG 2023',
    kind: 'vehicle-sale',
    category: 'Luxury',
    status: 'Active',
    priceLabel: 'AED 895,000',
    image: IMG.gwagon,
    images: [IMG.gwagon, IMG.landcruiser],
    description:
      'One owner from new, full Gargash service history and remaining warranty until 2027.',
    details: [
      d('Make', 'Mercedes-Benz'),
      d('Model', 'G 63 AMG'),
      d('Year', '2023'),
      d('Mileage', '18,400 km'),
      d('Transmission', 'Automatic'),
      d('Condition', 'Excellent'),
      d('Colour', 'Obsidian Black'),
    ],
    options: [
      {
        name: 'Payment',
        values: [
          {
            label: 'Cash',
            price: 'AED 895,000',
          },
          {
            label: 'Finance',
            price: 'AED 9,850/mo',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 95,
    talabPublished: true,
    leads: 22,
  },
  {
    id: 's2',
    title: 'Nissan Patrol Titanium 2022',
    kind: 'vehicle-sale',
    category: 'SUVs',
    status: 'Active',
    priceLabel: 'AED 232,000',
    image: IMG.patrol,
    images: [IMG.patrol],
    description:
      'Agency-maintained Patrol with seven seats, rear entertainment and a fresh 4-year service contract.',
    details: [
      d('Make', 'Nissan'),
      d('Model', 'Patrol Titanium'),
      d('Year', '2022'),
      d('Mileage', '62,100 km'),
      d('Transmission', 'Automatic'),
      d('Condition', 'Very good'),
    ],
    options: [
      {
        name: 'Payment',
        values: [
          {
            label: 'Cash',
            price: 'AED 232,000',
          },
          {
            label: 'Finance',
            price: 'AED 2,640/mo',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 91,
    talabPublished: true,
    leads: 16,
  },
  {
    id: 's3',
    title: 'BMW 530i M Sport 2021',
    kind: 'vehicle-sale',
    category: 'Sedans',
    status: 'Active',
    priceLabel: 'AED 148,500',
    image: IMG.bmw,
    images: [IMG.bmw],
    description:
      'Second owner, accident-free, with a recent major service and new tyres.',
    details: [
      d('Make', 'BMW'),
      d('Model', '530i M Sport'),
      d('Year', '2021'),
      d('Mileage', '74,800 km'),
      d('Transmission', 'Automatic'),
      d('Condition', 'Good'),
    ],
    options: [],
    missing: ['Add a full inspection report'],
    aiScore: 76,
    talabPublished: true,
    leads: 11,
  },
  {
    id: 's4',
    title: 'Tesla Model Y Long Range 2024',
    kind: 'vehicle-sale',
    category: 'Electric',
    status: 'Active',
    priceLabel: 'AED 189,000',
    image: IMG.tesla,
    images: [IMG.tesla],
    description:
      'Dual motor with 533 km range, autopilot and a transferable battery warranty.',
    details: [
      d('Make', 'Tesla'),
      d('Model', 'Model Y Long Range'),
      d('Year', '2024'),
      d('Mileage', '9,200 km'),
      d('Range', '533 km'),
      d('Condition', 'Excellent'),
    ],
    options: [],
    missing: [],
    aiScore: 88,
    talabPublished: true,
    leads: 19,
  },
  {
    id: 's5',
    title: 'Toyota Camry GLE 2023',
    kind: 'vehicle-sale',
    category: 'Sedans',
    status: 'Active',
    priceLabel: 'AED 89,000',
    image: IMG.camry,
    images: [IMG.camry],
    description: 'Single owner, agency serviced, ideal first family sedan.',
    details: [
      d('Make', 'Toyota'),
      d('Model', 'Camry GLE'),
      d('Year', '2023'),
      d('Mileage', '41,500 km'),
      d('Transmission', 'Automatic'),
    ],
    options: [],
    missing: ['Add photos of the interior'],
    aiScore: 72,
    talabPublished: false,
    leads: 4,
  },
  {
    id: 's6',
    title: 'Toyota Hilux Double Cab 2022',
    kind: 'vehicle-sale',
    category: 'Pickups',
    status: 'Draft',
    priceLabel: 'AED 96,000',
    image: IMG.van,
    images: [],
    description: '',
    details: [d('Make', 'Toyota'), d('Year', '2022')],
    options: [],
    missing: ['Add a description', 'Add mileage', 'Add more photos'],
    aiScore: 31,
    talabPublished: false,
    leads: 0,
  },
]

/* ------------------------------------------------------- properties for sale */

const PROPERTY_SALE: CatalogItem[] = [
  {
    id: 'ps1',
    title: '2-Bed Apartment · Dubai Marina',
    kind: 'property-sale',
    category: 'Apartments',
    status: 'Active',
    priceLabel: 'AED 2,450,000',
    image: IMG.apartment,
    images: [IMG.apartment, IMG.livingRoom],
    description:
      'High-floor two bedroom with full marina views, a fitted kitchen and two parking bays. Vacant on transfer.',
    details: [
      d('Bedrooms', '2'),
      d('Bathrooms', '3'),
      d('Size', '1,340 sq ft'),
      d('Community', 'Dubai Marina'),
      d('Furnishing', 'Unfurnished'),
      d('Parking', '2 bays'),
      d('View', 'Marina'),
      d('Ownership', 'Freehold'),
    ],
    options: [
      {
        name: 'Payment plan',
        values: [
          {
            label: 'Cash',
            price: 'AED 2,450,000',
          },
          {
            label: 'Mortgage',
            price: 'AED 11,200/mo',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 94,
    talabPublished: true,
    leads: 26,
  },
  {
    id: 'ps2',
    title: '5-Bed Villa · Arabian Ranches III',
    kind: 'property-sale',
    category: 'Villas',
    status: 'Active',
    priceLabel: 'AED 7,900,000',
    image: IMG.villa,
    images: [IMG.villa, IMG.livingRoom],
    description:
      'Corner plot with a private pool, landscaped garden and a maid’s room. Handed over in 2024.',
    details: [
      d('Bedrooms', '5'),
      d('Bathrooms', '6'),
      d('Size', '4,820 sq ft'),
      d('Community', 'Arabian Ranches III'),
      d('Handover', 'Completed 2024'),
      d('Parking', '3 bays'),
      d('Ownership', 'Freehold'),
    ],
    options: [],
    missing: ['Add the service charge'],
    aiScore: 81,
    talabPublished: true,
    leads: 18,
  },
  {
    id: 'ps3',
    title: '3-Bed Townhouse · Town Square',
    kind: 'property-sale',
    category: 'Townhouses',
    status: 'Active',
    priceLabel: 'AED 2,150,000',
    image: IMG.townhouse,
    images: [IMG.townhouse],
    description:
      'Family townhouse backing onto a park, with a 20 / 80 payment plan available from the developer.',
    details: [
      d('Bedrooms', '3'),
      d('Bathrooms', '4'),
      d('Size', '1,980 sq ft'),
      d('Community', 'Town Square'),
      d('Developer', 'Nshama'),
      d('Furnishing', 'Unfurnished'),
    ],
    options: [
      {
        name: 'Payment plan',
        values: [
          {
            label: 'Cash',
            price: 'AED 2,150,000',
          },
          {
            label: '20 / 80 plan',
            price: 'AED 430,000 down',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 90,
    talabPublished: true,
    leads: 12,
  },
  {
    id: 'ps4',
    title: 'Penthouse · Palm Jumeirah',
    kind: 'property-sale',
    category: 'Penthouses',
    status: 'Active',
    priceLabel: 'AED 24,000,000',
    image: IMG.penthouse,
    images: [IMG.penthouse],
    description:
      'Full-floor penthouse with a private lift lobby, 180° sea views and a wrap-around terrace.',
    details: [
      d('Bedrooms', '4'),
      d('Bathrooms', '5'),
      d('Size', '6,410 sq ft'),
      d('Community', 'Palm Jumeirah'),
      d('View', 'Sea and skyline'),
      d('Ownership', 'Freehold'),
    ],
    options: [],
    missing: ['Add a floor plan'],
    aiScore: 79,
    talabPublished: true,
    leads: 31,
  },
  {
    id: 'ps5',
    title: 'Fitted Office · Business Bay',
    kind: 'property-sale',
    category: 'Offices',
    status: 'Active',
    priceLabel: 'AED 1,680,000',
    image: IMG.office,
    images: [IMG.office],
    description:
      'Fully fitted 1,120 sq ft office with eight workstations, a meeting room and a pantry.',
    details: [
      d('Size', '1,120 sq ft'),
      d('Community', 'Business Bay'),
      d('Furnishing', 'Fitted'),
      d('Parking', '2 bays'),
    ],
    options: [],
    missing: [],
    aiScore: 86,
    talabPublished: false,
    leads: 5,
  },
  {
    id: 'ps6',
    title: 'Residential Plot · Al Furjan',
    kind: 'property-sale',
    category: '',
    status: 'Draft',
    priceLabel: 'AED 3,100,000',
    image: '',
    images: [],
    description: '',
    details: [d('Size', '5,400 sq ft')],
    options: [],
    missing: ['Add photos', 'Add a description', 'Choose a category'],
    aiScore: 24,
    talabPublished: false,
    leads: 0,
  },
]

/* ------------------------------------------------------- properties to rent */

const PROPERTY_RENT: CatalogItem[] = [
  {
    id: 'pr1',
    title: '1-Bed Apartment · JVC',
    kind: 'property-rent',
    category: 'Apartments',
    status: 'Active',
    priceLabel: 'AED 72,000/year',
    image: IMG.apartment,
    images: [IMG.apartment, IMG.livingRoom],
    description:
      'Bright one bedroom with a balcony, built-in wardrobes and access to a shared pool and gym.',
    details: [
      d('Bedrooms', '1'),
      d('Bathrooms', '2'),
      d('Size', '780 sq ft'),
      d('Community', 'Jumeirah Village Circle'),
      d('Furnishing', 'Unfurnished'),
      d('Parking', '1 bay'),
      d('Available from', '1 Oct 2026'),
      d('Cheques', 'Up to 4'),
    ],
    options: [
      {
        name: 'Payment terms',
        values: [
          {
            label: '1 cheque',
            price: 'AED 72,000',
          },
          {
            label: '4 cheques',
            price: 'AED 78,000',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 93,
    talabPublished: true,
    leads: 24,
  },
  {
    id: 'pr2',
    title: '4-Bed Villa · Mirdif',
    kind: 'property-rent',
    category: 'Villas',
    status: 'Active',
    priceLabel: 'AED 210,000/year',
    image: IMG.villa,
    images: [IMG.villa],
    description:
      'Independent villa with a private garden, maid’s room and covered parking for two cars.',
    details: [
      d('Bedrooms', '4'),
      d('Bathrooms', '5'),
      d('Size', '3,600 sq ft'),
      d('Community', 'Mirdif'),
      d('Furnishing', 'Unfurnished'),
      d('Available from', 'Immediately'),
      d('Cheques', 'Up to 2'),
    ],
    options: [
      {
        name: 'Payment terms',
        values: [
          {
            label: '1 cheque',
            price: 'AED 210,000',
          },
          {
            label: '2 cheques',
            price: 'AED 218,000',
          },
        ],
      },
    ],
    missing: [],
    aiScore: 89,
    talabPublished: true,
    leads: 15,
  },
  {
    id: 'pr3',
    title: 'Furnished Studio · Business Bay',
    kind: 'property-rent',
    category: 'Studios',
    status: 'Active',
    priceLabel: 'AED 68,000/year',
    image: IMG.studio,
    images: [IMG.studio],
    description:
      'Fully furnished studio with canal views, bills included and flexible short-term terms.',
    details: [
      d('Bedrooms', 'Studio'),
      d('Bathrooms', '1'),
      d('Size', '460 sq ft'),
      d('Community', 'Business Bay'),
      d('Furnishing', 'Furnished'),
      d('Available from', 'Immediately'),
    ],
    options: [
      {
        name: 'Lease length',
        values: [
          {
            label: '12 months',
            price: 'AED 68,000',
          },
          {
            label: '6 months',
            price: 'AED 39,000',
          },
        ],
      },
    ],
    missing: ['Confirm which bills are included'],
    aiScore: 77,
    talabPublished: true,
    leads: 9,
  },
  {
    id: 'pr4',
    title: '3-Bed Townhouse · Damac Hills 2',
    kind: 'property-rent',
    category: 'Townhouses',
    status: 'Active',
    priceLabel: 'AED 135,000/year',
    image: IMG.townhouse,
    images: [IMG.townhouse],
    description:
      'Community townhouse with a small garden, close to the clubhouse and school bus route.',
    details: [
      d('Bedrooms', '3'),
      d('Bathrooms', '4'),
      d('Size', '1,900 sq ft'),
      d('Community', 'Damac Hills 2'),
      d('Furnishing', 'Unfurnished'),
      d('Cheques', 'Up to 4'),
    ],
    options: [],
    missing: ['Add an available-from date'],
    aiScore: 74,
    talabPublished: true,
    leads: 6,
  },
  {
    id: 'pr5',
    title: 'Serviced Office · DIFC',
    kind: 'property-rent',
    category: 'Offices',
    status: 'Active',
    priceLabel: 'AED 320,000/year',
    image: IMG.office,
    images: [IMG.office],
    description:
      'Ten-desk serviced suite with reception, meeting-room credits and DED-approved licensing support.',
    details: [
      d('Size', '1,450 sq ft'),
      d('Community', 'DIFC'),
      d('Furnishing', 'Fitted and furnished'),
      d('Available from', '15 Sep 2026'),
    ],
    options: [],
    missing: [],
    aiScore: 87,
    talabPublished: true,
    leads: 11,
  },
  {
    id: 'pr6',
    title: '2-Bed Holiday Home · JBR',
    kind: 'property-rent',
    category: 'Short stay',
    status: 'Unavailable',
    priceLabel: 'AED 1,250/night',
    image: IMG.livingRoom,
    images: [IMG.livingRoom],
    description:
      'Beachfront holiday home let nightly, fully managed with linen and cleaning included. Booked until November.',
    details: [
      d('Bedrooms', '2'),
      d('Bathrooms', '2'),
      d('Size', '1,150 sq ft'),
      d('Community', 'Jumeirah Beach Residence'),
      d('Furnishing', 'Furnished'),
    ],
    options: [],
    missing: [],
    aiScore: 85,
    talabPublished: true,
    leads: 20,
  },
  {
    id: 'pr7',
    title: 'Studio · Dubai Silicon Oasis',
    kind: 'property-rent',
    category: '',
    status: 'Draft',
    priceLabel: 'AED 42,000/year',
    image: '',
    images: [],
    description: '',
    details: [],
    options: [],
    missing: [
      'Add photos',
      'Add a description',
      'Add the size',
      'Choose a category',
    ],
    aiScore: 18,
    talabPublished: false,
    leads: 0,
  },
]

export const CATALOG_SETS: {
  id: string
  label: string
  items: CatalogItem[]
}[] = [
  {
    id: 'rental',
    label: 'Car rental',
    items: RENTAL,
  },
  {
    id: 'cars-sale',
    label: 'Cars for sale',
    items: CARS_SALE,
  },
  {
    id: 'apparel',
    label: 'Apparel shop',
    items: APPAREL,
  },
  {
    id: 'grocery',
    label: 'Groceries',
    items: GROCERY,
  },
  {
    id: 'property-sale',
    label: 'Properties for sale',
    items: PROPERTY_SALE,
  },
  {
    id: 'property-rent',
    label: 'Properties to rent',
    items: PROPERTY_RENT,
  },
  {
    id: 'perfume',
    label: 'Perfume shop',
    items: PERFUME,
  },
  {
    id: 'restaurant',
    label: 'Restaurant',
    items: RESTAURANT,
  },
  {
    id: 'fastfood',
    label: 'Burger King',
    items: FASTFOOD,
  },
  {
    id: 'retail',
    label: 'Retail & grocery',
    items: RETAIL,
  },
  {
    id: 'empty',
    label: 'Empty catalogue',
    items: [],
  },
]
